import React, { useState, useCallback } from 'react';
import { connect, useDispatch } from "react-redux";
import { useTranslation } from 'react-i18next';
import { heroActions } from "../Store/Actions/heroActions";
import { gameDataActions } from '../Store/Actions/gameDataActions';
import { GAME_STATE, MODAL_FLAG } from '../Helpers/constants';
import { Btn, Icon, Label } from './Common/OkoylUI';

// Note: heroActions still used for movement / inventory dispatches below.
// Combat-time choices (attack / special) go through gameDataActions.fightData
// because that's what fightSaga listens for via take(IS_DATA_PLAYER_*).

export const COMMAND_BOX_FLAG = {
    FIGHT: 'fight',
    MOVE: 'move',
    GAME_OVER: 'game over',
    WAITING: 'waiting',
};

const FIGHT_FLAG = {
    ATTACK: 'attack',
};

const MANA_COST = {
    BASIC_ATTACK: 2,
    SPECIAL_ATTACK: 10,
};

const CommandBox = ({ flag, hero = null, map = null }) => {
    const safeHero = hero || {};
    const safeMap = map || {};
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [fightFlag, setFightFlag] = useState(null);

    const openInventory = () => {
        // Same flow as the in-game footer: MODAL_FLAG.INVENTORY (not
        // GAME_STATE.INVENTORY — that constant doesn't exist and the modal
        // switcher in Core.js matches against MODAL_FLAG.*).
        dispatch(gameDataActions.modalData.set(true, MODAL_FLAG.INVENTORY));
        dispatch(gameDataActions.setGameState(GAME_STATE.MODAL));
    };

    const fightBoxAttack = useCallback(() => {
        const currentMana = safeHero.stats?.mana ?? 0;
        const heroClass = (safeHero.classType || '').toUpperCase() === 'WARRIOR'
            ? 'SWORDER'
            : (safeHero.classType || '').toUpperCase();
        const hasEnoughManaForSpecial = currentMana >= MANA_COST.SPECIAL_ATTACK;

        const specialAttacks = {
            ARCHER:  { name: t('commandBox.specialAttacks.archer'),  type: 'POISON',            manaCost: MANA_COST.SPECIAL_ATTACK },
            MAGE:    { name: t('commandBox.specialAttacks.mage'),    type: 'STUN',              manaCost: MANA_COST.SPECIAL_ATTACK },
            SWORDER: { name: t('commandBox.specialAttacks.sworder'), type: 'ARMOR_PENETRATION', manaCost: MANA_COST.SPECIAL_ATTACK },
            TANK:    { name: t('commandBox.specialAttacks.tank'),    type: 'DEFENSE_UP',        manaCost: MANA_COST.SPECIAL_ATTACK },
        };

        const handleAttack = (attackType) => {
            if (attackType === 'basic' && currentMana >= MANA_COST.BASIC_ATTACK) {
                dispatch(gameDataActions.fightData.isDataPlayerAttack({
                    type: 'BASIC',
                    manaCost: MANA_COST.BASIC_ATTACK,
                }));
            } else if (attackType === 'special' && hasEnoughManaForSpecial) {
                const chosen = specialAttacks[heroClass];
                if (chosen) {
                    dispatch(gameDataActions.fightData.isDataPlayerSpecialAttack({
                        type: chosen.type,
                        manaCost: chosen.manaCost,
                    }));
                }
            } else {
                return;
            }
            setFightFlag(null);
        };

        const specialAttack = specialAttacks[heroClass];

        return (
            <div className="okcard" style={{ marginTop: 0 }}>
                <Label color="gold">{t('commandBox.labels.chooseAttack')}</Label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
                    <Btn variant="primary" full onClick={() => handleAttack('basic')}>
                        {t('commandBox.actions.basicAttack')} · {MANA_COST.BASIC_ATTACK} PM
                    </Btn>
                    {specialAttack && (
                        <Btn
                            variant="neant"
                            full
                            disabled={!hasEnoughManaForSpecial}
                            onClick={() => hasEnoughManaForSpecial && handleAttack('special')}
                        >
                            {specialAttack.name} · {specialAttack.manaCost} PM
                        </Btn>
                    )}
                </div>
                <div style={{ marginTop: 10, textAlign: 'center' }}>
                    <Btn variant="ghost" small onClick={() => setFightFlag(null)}>
                        {t('commandBox.actions.back')}
                    </Btn>
                </div>
            </div>
        );
    }, [t, dispatch, safeHero.stats?.mana, safeHero.classType]);

    if (!flag) return null;

    switch (flag) {
        case COMMAND_BOX_FLAG.FIGHT: {
            if (fightFlag === FIGHT_FLAG.ATTACK) return fightBoxAttack();

            return (
                <div className="okcard" style={{ marginTop: 0 }}>
                    <Label color="gold">{t('commandBox.labels.action')}</Label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
                        <Btn variant="danger" full onClick={() => setFightFlag(FIGHT_FLAG.ATTACK)}>
                            {t('commandBox.actions.attack')}
                        </Btn>
                        <Btn variant="secondary" full onClick={openInventory}>
                            {t('commandBox.actions.inventory')}
                        </Btn>
                        <Btn variant="ghost" full onClick={() => dispatch(gameDataActions.fightData.isDataPlayerEscape({ escape: true }))}>
                            {t('commandBox.actions.flee')}
                        </Btn>
                    </div>
                </div>
            );
        }
        case COMMAND_BOX_FLAG.MOVE: {
            const heroPosition = safeHero.position || { x: 0, y: 0 };
            const mapData = safeMap || { rows: 10, columns: 10 };
            const canMoveUp    = heroPosition.y > 0;
            const canMoveDown  = heroPosition.y < (mapData?.rows || 10) - 1;
            const canMoveLeft  = heroPosition.x > 0;
            const canMoveRight = heroPosition.x < (mapData?.columns || 10) - 1;

            const padBtn = (name, enabled, dx, dy) => (
                <Btn
                    variant="secondary"
                    small
                    disabled={!enabled}
                    onClick={() => enabled && dispatch(heroActions.move(dx, dy))}
                >
                    <Icon name={name} size={14} />
                </Btn>
            );

            return (
                <div className="okcard" style={{ marginTop: 0 }}>
                    <Label color="gold">{t('commandBox.labels.movement')}</Label>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 12 }}>
                        {padBtn('chevron-up', canMoveUp, 0, -1)}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 4 }}>
                        {padBtn('chevron-left',  canMoveLeft,  -1, 0)}
                        {padBtn('chevron-down',  canMoveDown,   0, 1)}
                        {padBtn('chevron-right', canMoveRight,  1, 0)}
                    </div>
                    <div style={{ marginTop: 14 }}>
                        <Btn variant="secondary" full onClick={openInventory}>
                            <Icon name="backpack" size={14} /> {t('commandBox.actions.inventory')}
                        </Btn>
                    </div>
                </div>
            );
        }
        case COMMAND_BOX_FLAG.GAME_OVER: {
            return (
                <div className="okcard" style={{ marginTop: 16, textAlign: 'center' }}>
                    <span className="oklore" style={{ color: 'var(--crit)' }}>{t('commandBox.states.gameOver')}</span>
                </div>
            );
        }
        default: {
            return (
                <div className="okcard" style={{ marginTop: 16, textAlign: 'center' }}>
                    <span className="oklore oklore-sm">{t('commandBox.states.waiting')}</span>
                </div>
            );
        }
    }
};

const mapStateToProps = (state) => ({
    hero: state.hero,
    map: state.gameData.map,
});

export default connect(mapStateToProps)(CommandBox);
